import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxCustomAddress,
  OnyxPhone,
  OnyxRoutePoint,
  OnyxTab,
  OnyxTime,
} from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxIconBoxComponent,
  OnyxIconComponent,
  OnyxPhoneNumberInputComponent,
  OnyxSuggestionsComponent,
  OnyxTabsComponent,
  OnyxTextareaComponent,
  onyxTextareaLengthValidator,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxTimepickerComponent,
} from '@onyx/angular';
import { omit } from 'lodash';
import { filter, Subject } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import { SuggestionHelper } from '../../../../../common/helpers/suggestions.helper';
import { TransitHelper } from '../../../../../common/helpers/transit.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { TransitRoute } from '../../../../../common/interfaces/transit/transit-route';
import { OrderPointCategory } from '../../../common/enums/order-point-category';
import type { OrderPointTypeByCategory } from '../../../common/enums/order-point-type';
import { OrderTransitPointTimeWindowType } from '../../../common/enums/order-transit-point-time-window-type';

export type OrderTransitFormGroup = ReturnType<
  typeof OrderTransitFormComponent.buildForm
>;

@Component({
  selector: 'app-order-transit-form',
  imports: [
    ReactiveFormsModule,
    OnyxIconBoxComponent,
    OnyxIconComponent,
    OnyxAddressInputComponent,
    OnyxTextFieldComponent,
    OnyxTimepickerComponent,
    OnyxPhoneNumberInputComponent,
    OnyxTabsComponent,
    OnyxDatepickerComponent,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxDropdownComponent,
    OnyxTextareaComponent,
  ],
  templateUrl: './order-transit-form.component.html',
  styleUrl: './order-transit-form.component.scss',
})
export class OrderTransitFormComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly injector = inject(Injector);
  private readonly transitHelper = inject(TransitHelper);

  public readonly form = input.required<OrderTransitFormGroup>();
  public readonly disabled = input.required<boolean>();
  public readonly compact = input(false);

  protected readonly I18N = 'orders.orderForm.points';
  protected readonly TRANSIT_POINT_TIME_WINDOW_TYPES: OnyxTab<OrderTransitPointTimeWindowType>[] =
    [
      { name: '24/7', value: OrderTransitPointTimeWindowType.ALL_TIME },
      { name: 'Fix', value: OrderTransitPointTimeWindowType.FIX },
    ];
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
    { days: 3 },
  );

  protected routesSource = computed(() =>
    this.transitHelper.getRoutesSource(this.form().controls.type.value),
  );
  protected serviceTimeLabel = computed(
    () => `forms.serviceTime.${this.form().controls.type.value}`,
  );

  protected route$ = new Subject<TransitRoute | null>();

  public ngOnInit(): void {
    const controls = this.form().controls;

    if (this.disabled()) {
      ValidationHelper.disableControls(
        Object.values(omit(controls, 'includeInOrder')),
        { reset: false },
      );
    }

    this.route$.pipe(filter((route) => route != null)).subscribe((route) =>
      this.form().patchValue({
        startAddress: route.from.address,
        endAddress: route.to.address,
        serviceTime: route.duration,
        phone: route.from.phone,
      }),
    );

    const timeWindowForm = controls.timeWindow;
    const timeWindowControls = timeWindowForm.controls;
    const timeWindowType = toSignal(timeWindowControls.type.valueChanges, {
      initialValue: timeWindowControls.type.value,
      injector: this.injector,
    });

    effect(
      () => {
        if (this.disabled()) return;

        const type = timeWindowType();
        setTimeout(() =>
          ValidationHelper.toggleControls(
            [timeWindowControls.date, timeWindowControls.time],
            {
              enabled: type === OrderTransitPointTimeWindowType.FIX,
              reset: true,
            },
          ),
        );
      },
      { injector: this.injector },
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    type: OrderPointTypeByCategory[OrderPointCategory.TRANSIT],
    includeInOrder: boolean,
    isAlwaysOpen: boolean,
  ) {
    return fb.group({
      uuid: fb.control(uuidv7()),
      category: fb.control(OrderPointCategory.TRANSIT as const),
      type: fb.control(type),
      includeInOrder: fb.control(includeInOrder),
      name: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      driverNote: fb.control<string | null>(null, [
        onyxTextareaLengthValidator,
      ]),
      startAddress: fb.control<OnyxCustomAddress | null>({
        value: null,
        disabled: true,
      }),
      endAddress: fb.control<OnyxCustomAddress | null>({
        value: null,
        disabled: true,
      }),
      serviceTime: fb.control<number | null>({
        value: null,
        disabled: true,
      }),
      phone: fb.control<OnyxPhone | null>({
        value: null,
        disabled: true,
      }),
      timeWindow: fb.group({
        type: fb.control(
          isAlwaysOpen
            ? OrderTransitPointTimeWindowType.ALL_TIME
            : OrderTransitPointTimeWindowType.FIX,
          [Validators.required],
        ),
        date: fb.control<string | null>(null, [Validators.required]),
        time: fb.control<OnyxTime | null>(null, [Validators.required]),
      }),
      route: fb.control<OnyxRoutePoint | null>(null),
    });
  }
}
